import {
  cacheNativeLivePingCredentials,
  getNativeLivePingCredentialEpoch,
  type CachedNativeLivePingCredentials,
} from './native-live-ping-credential-cache'

interface IdTokenLike {
  payload?: { exp?: unknown }
  toString(): string
}

export interface SessionTokensLike {
  tokens?: { idToken?: IdTokenLike }
}

function idTokenExpiresAt(token: IdTokenLike | undefined): number | null {
  const exp = token?.payload?.exp
  return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : null
}

/**
 * The testable core of `getNativeLivePingCredentials` (Amplify and
 * `import.meta` stay in `sessions-api-env.ts`). Fetch an ID token with at
 * least `minRemainingMs` left (force-refreshing otherwise), cache it for the
 * travel tap — but ONLY if no sign-out happened while the fetch was in
 * flight: the epoch is captured first, and a changed epoch yields `null`
 * (nothing cached, nothing handed to the caller to arm native with).
 */
export async function fetchNativeLivePingCredentials(input: {
  fetchSession: (options?: { forceRefresh?: boolean }) => Promise<SessionTokensLike>
  apiBaseUrl: string
  now: number
  minRemainingMs: number
}): Promise<CachedNativeLivePingCredentials | null> {
  const epoch = getNativeLivePingCredentialEpoch()
  let token = (await input.fetchSession()).tokens?.idToken
  let expiresAt = idTokenExpiresAt(token)
  if (!token || expiresAt == null || expiresAt - input.now < input.minRemainingMs) {
    token = (await input.fetchSession({ forceRefresh: true })).tokens?.idToken
    expiresAt = idTokenExpiresAt(token)
  }
  if (!token || expiresAt == null || expiresAt <= input.now) return null
  if (epoch !== getNativeLivePingCredentialEpoch()) return null
  const creds = { apiBaseUrl: input.apiBaseUrl, idToken: token.toString(), expiresAt }
  cacheNativeLivePingCredentials(creds, epoch)
  return creds
}
